'use client';

import clsx from 'clsx';
import { useInView } from 'framer-motion';
import { type ReactNode, useRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import BaseContainer, {
  type BaseContainerProps,
  type DivPropsWithoutHidden,
} from '@commons/containers/BaseContainer';
import Flex, { type FlexItemProps, type FlexProps } from '@commons/containers/Flex';
import Grid, { type GridItemProps, type GridProps } from '@commons/containers/Grid';

import styles from './index.module.scss';

// Union of components that can be animated with the `withAnimation` HOC.

type Container =
  | typeof BaseContainer
  | typeof Flex
  | typeof Flex.Item
  | typeof Grid
  | typeof Grid.Item;

// Type definition for the available animations.

type Animation = 'fade-slide-up';

// Type definition of the properties added by the `withAnimation` HOC.

type AnimationProps = DivPropsWithoutHidden & {
  children: ReactNode;
  className?: string;
  animation?: Animation;
};

/**
 * Higher-order component that sets the `useAnimation` hook and applies the animation classes.
 *
 * @param Component - The container component to be animated.
 *
 * @returns The animated container component.
 */

export function withAnimation<Props>(Component: Container) {
  const AnimatedContainer = (props: Props & AnimationProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const animate = useInView(ref, { once: true });

    const { children, className, animation = 'fade-slide-up', ref: forwardedRef, ...rest } = props;

    const mergedRefs = mergeRefs([ref, forwardedRef]);

    const classes = clsx(className, styles.container, styles[animation], animate && styles.animate);

    return (
      <Component ref={mergedRefs} className={classes} {...rest}>
        {children}
      </Component>
    );
  };

  return AnimatedContainer;
}

/**
 * BaseContainer extended with the `withAnimation` HOC.
 *
 * Exported so that it can be used in a server component.
 */

export const AnimatedBaseContainer = withAnimation<BaseContainerProps>(BaseContainer);

/**
 * Flex extended with the `withAnimation` HOC.
 *
 * Exported so that it can be used in a server component.
 */

export const AnimatedFlex = withAnimation<FlexProps>(Flex);

/**
 * FlexItem extended with the `withAnimation` HOC.
 *
 * Exported so that it can be used in a server component.
 */

export const AnimatedFlexItem = withAnimation<FlexItemProps>(Flex.Item);

/**
 * Grid extended with the `withAnimation` HOC.
 *
 * Exported so that it can be used in a server component.
 */

export const AnimatedGrid = withAnimation<GridProps>(Grid);

/**
 * GridItem extended with the `withAnimation` HOC.
 *
 * Exported so that it can be used in a server component.
 */

export const AnimatedGridItem = withAnimation<GridItemProps>(Grid.Item);
